/** SUNTRONIX ONE — Deployment Case detail page.
 *  Shows the full deployment lifecycle for ONE connected case:
 *    - Progress stepper (current stage highlighted)
 *    - Current owner card + Complete-stage action
 *    - Case timeline + email log
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import RecordStickyNotes from "@/components/app/RecordStickyNotes";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Clock, Send, User, Mail, ArrowRight, Star, Package, Truck } from "lucide-react";
import { Input } from "@/components/ui/input";

export default function DeploymentCase() {
  const { caseId } = useParams();
  const [data, setData] = useState(null);
  const [emails, setEmails] = useState([]);
  const [notes, setNotes] = useState("");
  const [stagePayload, setStagePayload] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: caseData }, { data: emailData }] = await Promise.all([
        api.get(`/cases/${caseId}`),
        api.get(`/notifications/case/${caseId}/log`),
      ]);
      setData(caseData);
      setEmails(emailData.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load case");
    }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const complete = async () => {
    setBusy(true);
    try {
      const stage = data.case.current_stage;
      const { data: res } = await api.post(`/cases/${caseId}/complete-stage`,
        { stage, notes, payload: stagePayload });
      toast.success(res.current_stage === "completed"
        ? "Case completed."
        : `Handed off to ${res.current_owner || res.current_stage}`);
      setNotes("");
      setStagePayload({});
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to complete stage");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="p-6 text-sm text-slate-500" data-testid="case-loading">Loading case…</div>;
  const { case: c, stage_labels: labels } = data;
  const completed = new Set((c.completed_stages || []).map((s) => s.stage));

  return (
    <div className="max-w-[1400px] mx-auto space-y-6" data-testid="case-detail">
      <PageHeader
        eyebrow="Operations · Deployment case"
        title={`${c.case_code} — ${labels[c.current_stage] || c.current_stage}`}
        context={`${c.customer} · ${c.employee_email}`}
        actions={
          <Link to={`/deployments/${c.request_id}/pre-acceptance`}
                className="st-btn st-btn-sm st-btn-ghost" data-testid="case-open-request">
            Open request
          </Link>
        }
        testId="case-header"
      />

      <Section title="Progress" testId="case-progress">
        <div className="st-panel p-4 overflow-x-auto">
          <div className="flex items-center gap-1 min-w-max">
            {(c.stages || []).map((s, i) => {
              const isCurrent = s === c.current_stage;
              const isDone = completed.has(s);
              const cls = isDone
                ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                : isCurrent
                  ? "bg-[color:var(--st-accent)]/10 text-[color:var(--st-accent)] border-[color:var(--st-accent)]"
                  : "bg-slate-50 text-slate-500 border-slate-200";
              return (
                <div key={s} className="flex items-center gap-1" data-testid={`stage-${s}`}>
                  <div className={`px-3 py-1.5 text-[11px] rounded-full border ${cls}`}>
                    {isDone && <CheckCircle2 className="inline h-3 w-3 mr-1" strokeWidth={2} />}
                    {isCurrent && !isDone && <Clock className="inline h-3 w-3 mr-1" strokeWidth={2} />}
                    {labels[s] || s}
                  </div>
                  {i < c.stages.length - 1 && <ArrowRight className="h-3 w-3 text-slate-300" strokeWidth={2} />}
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Timeline" hint="Every action on this case, in order." testId="case-timeline">
            <div className="st-panel divide-y divide-[color:var(--st-border)]">
              {(c.timeline || []).slice().reverse().map((t) => (
                <div key={t.id} className="p-3 text-[12px]" data-testid={`timeline-${t.id}`}>
                  <div className="flex items-center gap-2">
                    <Clock className="h-3 w-3 text-slate-400" strokeWidth={1.6} />
                    <span className="font-medium">{labels[t.stage] || t.kind}</span>
                    <span className="text-slate-500">· {t.actor} · {t.at ? new Date(t.at).toLocaleString() : ""}</span>
                  </div>
                  {t.detail && <div className="mt-1 text-slate-600 pl-5">{t.detail}</div>}
                </div>
              ))}
            </div>
          </Section>

          <Section title="Notes on this deployment" hint="Personal or team notes attached to this case." testId="case-sticky-notes">
            <RecordStickyNotes
              contextType="deployment"
              contextId={c.id}
              title="Deployment notes"
            />
          </Section>

          <Section title="Email log" hint="MOCKED delivery — records what would have been sent." testId="case-emails">
            <div className="st-panel divide-y divide-[color:var(--st-border)]">
              {emails.length === 0 && (
                <div className="p-3 text-[12px] text-slate-500">No comms yet.</div>
              )}
              {emails.map((m) => (
                <div key={m.id} className="p-3 text-[12px]" data-testid={`email-${m.id}`}>
                  <div className="flex items-center gap-2">
                    <Mail className="h-3 w-3 text-slate-400" strokeWidth={1.6} />
                    <span className="font-medium">{m.subject}</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] bg-amber-50 text-amber-800">{m.delivery_status || "MOCKED"}</span>
                  </div>
                  <div className="pl-5 mt-0.5 text-slate-600">to {m.recipient} · {m.sent_at ? new Date(m.sent_at).toLocaleString() : ""}</div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <div className="space-y-4">
          <Section title="Current owner" testId="case-owner">
            <div className="st-panel p-3 text-[12px]" data-testid="owner-card">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-slate-500" strokeWidth={1.6} />
                <span className="font-medium">{c.current_owner || "—"}</span>
              </div>
              <div className="mt-1 text-slate-600">Stage: {labels[c.current_stage] || c.current_stage}</div>
              <div className="mt-1 text-slate-500 text-[11px]">Status: {c.status}</div>
            </div>
            {c.status === "open" && (
              <div className="st-panel p-3 mt-2 space-y-2">
                <StageActionFields stage={c.current_stage} payload={stagePayload} setPayload={setStagePayload} />
                <label className="text-[11px] text-slate-600">Handoff notes (optional)</label>
                <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="case-notes" />
                <Button onClick={complete} disabled={busy} className="mt-2 w-full"
                  data-testid="case-complete-stage"
                  style={{ background: "var(--st-accent)", color: "white" }}>
                  <Send className="h-4 w-4 mr-1.5" strokeWidth={1.6} />
                  {busy ? "Working…" : `Complete ${labels[c.current_stage] || c.current_stage}`}
                </Button>
              </div>
            )}
            {c.status === "closed" && (
              <FeedbackCapture caseId={c.id} existing={c.feedback_employee || c.feedback_customer} />
            )}
          </Section>

          <Section title="Case metadata" testId="case-meta">
            <div className="st-panel p-3 space-y-1 text-[12px]">
              <div><span className="text-slate-500">Customer:</span> {c.customer}</div>
              <div><span className="text-slate-500">Employee:</span> {c.employee_email}</div>
              <div><span className="text-slate-500">Opened:</span> {c.opened_at ? new Date(c.opened_at).toLocaleString() : "—"}</div>
              <div><span className="text-slate-500">Opened by:</span> {c.opened_by || "—"}</div>
              {c.closed_at && <div><span className="text-slate-500">Closed:</span> {new Date(c.closed_at).toLocaleString()}</div>}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// StageActionFields — compact stage-specific inputs (§28-35).
// Back Office captures courier + AWB.  Store captures pack details.
// ------------------------------------------------------------------
function StageActionFields({ stage, payload, setPayload }) {
  const set = (k, v) => setPayload((p) => ({ ...p, [k]: v }));
  if (stage === "back_office") {
    return (
      <div className="space-y-2" data-testid="stage-back-office">
        <div className="text-[11px] text-slate-500 flex items-center gap-1">
          <Truck className="h-3 w-3" strokeWidth={1.6} /> Back Office · Label creation
        </div>
        <Input placeholder="Courier (e.g. Blue Dart)" value={payload.courier || ""}
          onChange={(e) => set("courier", e.target.value)} data-testid="bo-courier" />
        <Input placeholder="AWB / Tracking number" value={payload.awb || ""}
          onChange={(e) => set("awb", e.target.value)} data-testid="bo-awb" />
        <Input placeholder="Label URL (optional)" value={payload.label_url || ""}
          onChange={(e) => set("label_url", e.target.value)} data-testid="bo-label-url" />
      </div>
    );
  }
  if (stage === "store" || stage === "shipped") {
    return (
      <div className="space-y-2" data-testid="stage-store">
        <div className="text-[11px] text-slate-500 flex items-center gap-1">
          <Package className="h-3 w-3" strokeWidth={1.6} /> Store · Pick · Pack · Dispatch
        </div>
        <Input placeholder="Picked serial (comma-separated)" value={payload.picked_serials || ""}
          onChange={(e) => set("picked_serials", e.target.value)} data-testid="store-serials" />
        <Input placeholder="Package weight (kg)" value={payload.weight || ""}
          onChange={(e) => set("weight", e.target.value)} data-testid="store-weight" />
        <Input placeholder="Accessories confirmed (Y/N)" value={payload.accessories || ""}
          onChange={(e) => set("accessories", e.target.value)} data-testid="store-accessories" />
      </div>
    );
  }
  if (stage === "delivered") {
    return (
      <div className="space-y-2" data-testid="stage-delivered">
        <div className="text-[11px] text-slate-500">Deployment verification</div>
        <Input placeholder="Employee acknowledgement (Y/N)" value={payload.acknowledged || ""}
          onChange={(e) => set("acknowledged", e.target.value)} data-testid="verify-ack" />
      </div>
    );
  }
  return null;
}

// ------------------------------------------------------------------
// FeedbackCapture — visible after the case closes.  Stars + comment.
// ------------------------------------------------------------------
function FeedbackCapture({ caseId, existing }) {
  const [rating, setRating] = useState(existing?.rating || 0);
  const [comments, setComments] = useState(existing?.comments || "");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(!!existing);
  const submit = async () => {
    if (!rating) { toast.error("Please give a rating"); return; }
    setSaving(true);
    try {
      await api.post(`/feedback/case/${caseId}`, {
        rating, comments, from_role: "customer",
      });
      toast.success("Thanks — feedback captured");
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit feedback");
    } finally { setSaving(false); }
  };
  if (done) {
    return (
      <div className="st-panel p-3 mt-2 text-[12px] bg-emerald-50 text-emerald-800" data-testid="feedback-done">
        <Star className="inline h-3 w-3 mr-1" fill="currentColor" strokeWidth={1.6} />
        Feedback recorded · {rating}/5
      </div>
    );
  }
  return (
    <div className="st-panel p-3 mt-2 space-y-2" data-testid="feedback-capture">
      <div className="text-[12px] font-medium">How was the deployment?</div>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => setRating(n)} data-testid={`feedback-star-${n}`}
            className={n <= rating ? "text-amber-500" : "text-slate-300"}>
            <Star className="h-5 w-5" fill={n <= rating ? "currentColor" : "none"} strokeWidth={1.6} />
          </button>
        ))}
      </div>
      <Textarea rows={2} placeholder="Any comments?" value={comments}
        onChange={(e) => setComments(e.target.value)} data-testid="feedback-comments" />
      <Button onClick={submit} disabled={saving} className="w-full" data-testid="feedback-submit"
        style={{ background: "var(--st-accent)", color: "white" }}>
        {saving ? "Submitting…" : "Submit feedback"}
      </Button>
    </div>
  );
}
